import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {BookOfShadowsGameConfig} from "../../BookOfShadowsGameConfig";
import {TweenLite} from "gsap";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {TextStyleOptions} from "@nolimitcity/slot-game/bin/core/text/TextStyleOptions";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {BitmapCountUp, IBitmapCountUpConfig} from "@nolimitcity/slot-game/bin/core/component/BitmapCountUp";
import {BookOfShadowsTransitionsView} from "../BookOfShadowsTransitionsView";
import {BookOfShadows} from "../../BookOfShadows";

export class BookOfShadowsReTriggerView extends BookOfShadowsTransitionsView {
    private _centerPos: number[] = [BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2, (BookOfShadowsGameConfig.instance.REEL_AREA_HEIGHT / 2) + BookOfShadowsGameConfig.instance.REEL_AREA_POS_Y];
    private _scatterSpine: PIXI.spine.Spine;
    private _selectedSymbol: PIXI.Sprite;
    private _shuffleShadowAnim: TimelineSprite;

    private _youWon: PIXI.Text;
    private _fsAmountItem: BitmapCountUp;
    private _additionalFreeSpin: PIXI.Text;

    /**
     * @constructs
     */
    constructor(layer: ScalableLayer, callBack: () => void) {
        super(layer, callBack);

        this._layer.addChild(this);
    }

    /**
     * @description To clean up the view.
     */
    public cleanUp(): void {
        this._scatterSpine.visible = false;
        this._selectedSymbol.visible = false;
        this._shuffleShadowAnim.visible = false;
        this._shuffleShadowAnim.stopLoop();
        this._youWon.visible = false;
        this._fsAmountItem.visible = false;
        this._additionalFreeSpin.visible = false;
    }

    /**
     * @description To play the scatter re-trigger spine animation.
     * @param symbol
     * @param extraFS
     */
    public playScatterReTriggerAnimation(symbol: string, extraFS: number): void {
        this.createScatterSpine();

        let tween: SpineTween = new SpineTween(this._scatterSpine, 0, "S_Trigger", 0);
        TweenLite.fromTo(this._scatterSpine, 0.001, {alpha: 0}, {alpha: 1});

        TweenLite.to(() => {
        }, tween.duration() + 0.2, {
            onComplete: () => {
                tween.clear();
                tween.kill();
                this.turnOnItems(symbol, extraFS);
            }
        });
    }

    /**
     * @inheritDoc
     */
    protected initAnimations(): void {
        super.initAnimations();

        this._shuffleShadowAnim = this.createShuffleAnim("shuffleShadow");
        this._shuffleShadowAnim.blendMode = PIXI.BLEND_MODES.MULTIPLY;
        this._shuffleShadowAnim.scale.set(2);

        this._selectedSymbol = new PIXI.Sprite(PIXI.Texture.EMPTY);
        this._selectedSymbol.visible = false;
        this._selectedSymbol.position.set(BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2 - 200, (BookOfShadowsGameConfig.instance.GAME_HEIGHT / 2) - 80);
        this._selectedSymbol.anchor.set(0.5);
        this.addChild(this._selectedSymbol);

        this.createTextItems();
    }

    /**
     * @description To make the remaining items visible on the screen.
     * @param symbol
     * @param extraFS
     */
    private turnOnItems(symbol: string, extraFS: number): void {
        this._selectedSymbol.texture = GameResources.getTextures(symbol + "/" + symbol + "Gold")[0];
        this._selectedSymbol.visible = true;

        this._shuffleShadowAnim.visible = true;
        this._shuffleShadowAnim.playLoop();

        this._fsAmountItem.getCountUpAnimation(extraFS, extraFS, {duration: 0});
        this._fsAmountItem.pivot.set(this._fsAmountItem.width / 2, this._fsAmountItem.height / 2);

        this._fsAmountItem.visible = true;

        this._youWon.visible = true;
        this._additionalFreeSpin.visible = true;

        TweenLite.to(() => {
        }, 4, {onComplete: () => this._completeCallback()});
    }

    /**
     * @description To create all the game graphics for this view.
     */
    private createTextItems(): void {
        const x: number = BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2 + 160,
            y: number = (BookOfShadowsGameConfig.instance.GAME_HEIGHT / 2) - 150;

        this._youWon = this.createTextItem("You won", 22, FontWeight.BOLD);
        this._youWon.position.set(x, y);

        this.createAmountItems(x);

        this._additionalFreeSpin = this.createTextItem("additional Free Spins!", 22, FontWeight.BOLD);
        this._additionalFreeSpin.position.set(x, (this._fsAmountItem.y + this._fsAmountItem.height / 2 + 30));
    }

    /**
     * @description To create the scatter spine.
     */
    private createScatterSpine(): void {
        this._scatterSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("S_spine"));
        this._scatterSpine.position.set(this._centerPos[0], this._centerPos[1]);
        this.addChildAt(this._scatterSpine, 0);
    }

    /**
     * @description To create the shuffle animation.
     * @param assetName
     */
    private createShuffleAnim(assetName: string): TimelineSprite {
        const item: TimelineSprite = new TimelineSprite(GameResources.getTextures(assetName));
        item.visible = false;
        item.position.set(BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2 - 200, (BookOfShadowsGameConfig.instance.GAME_HEIGHT / 2) - 80);
        item.anchor.set(0.5);
        this.addChild(item);

        return item;
    }

    /**
     * @description To create the text item.
     * @param text
     * @param size
     * @param fontWeight
     */
    private createTextItem(text: string, size: number, fontWeight: FontWeight): PIXI.Text {
        const style: TextStyleOptions = {
                fontFamily: "Open Sans",
                fontWeight: fontWeight,
                fill: "#000",
                fontSize: size
            },
            item: PIXI.Text = new PIXI.Text(BookOfShadows.api.translations.translate(text), style);

        item.visible = false;
        item.anchor.set(0.5);
        this.addChild(item);

        return item;
    }

    /**
     * @description To create the amount item.
     */
    private createAmountItem(): BitmapCountUp {
        const style: IBitmapCountUpConfig = {
                style: {
                    font: {
                        size: 20,
                        name: "NumbersWinCountUp"
                    },
                    tint: parseInt(("#ffffff").replace(/^#/, ''), 16)
                },
                numberFormat: {
                    precision: 0
                }
            },
            item: BitmapCountUp = new BitmapCountUp(style);

        item.visible = false;
        this.addChild(item);

        return item;
    }

    /**
     * @description To create all the amount items.
     * @param x
     */
    private createAmountItems(x: number): void {
        const centerItem: BitmapCountUp = this.createAmountItem();
        centerItem.position.set(x, (this._youWon.y + this._youWon.height / 2 + 40));
        centerItem.tint = 0xffffff;

        this._fsAmountItem = centerItem;
    }
}
